/**
 * Locale configuration.
 *
 * English lives at the site root (`/blog`, `/privacy-policy`) so the URLs that
 * are already indexed keep working. Every other locale is served under a path
 * prefix (`/tr/blog`, `/es/blog`). Firebase Hosting runs with trailingSlash
 * false, so a locale home is `/tr`, never `/tr/`.
 */

export const DEFAULT_LOCALE = 'en';

export const LOCALES = ['en', 'tr', 'es', 'fr', 'de', 'it', 'nl'];

/** Language names written in their own language, for the switcher. */
export const LOCALE_NAMES = {
  en: 'English',
  tr: 'Türkçe',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  nl: 'Nederlands',
};

export const LOCALE_FLAGS = {
  en: '🇬🇧',
  tr: '🇹🇷',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  nl: '🇳🇱',
};

/** hreflang values. Plain language codes: we target languages, not countries. */
export const HREFLANG = {
  en: 'en',
  tr: 'tr',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  nl: 'nl',
};

export const OG_LOCALES = {
  en: 'en_US',
  tr: 'tr_TR',
  es: 'es_ES',
  fr: 'fr_FR',
  de: 'de_DE',
  it: 'it_IT',
  nl: 'nl_NL',
};

/** Intl tags used for date formatting. */
export const DATE_LOCALES = {
  en: 'en-US',
  tr: 'tr-TR',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
  nl: 'nl-NL',
};

export const STORAGE_KEY = 'manifestify_locale';

export function isLocale(value) {
  return LOCALES.includes(value);
}

/**
 * Builds a URL path for a locale from a canonical (English) route path.
 * localePath('en', '/blog') -> '/blog'
 * localePath('tr', '/blog') -> '/tr/blog'
 * localePath('tr', '/')     -> '/tr'
 */
export function localePath(locale, path = '/') {
  const clean = path === '/' ? '' : path;
  if (locale === DEFAULT_LOCALE) return clean || '/';
  return `/${locale}${clean}`;
}

/** Reads the locale the visitor picked last time, if any. */
export function getStoredLocale() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeLocale(locale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* Safari private mode and friends. Preference just won't persist. */
  }
}

/**
 * Best supported locale for this visitor: an explicit past choice wins over
 * the browser's language list. Returns null when neither matches, so callers
 * can leave the visitor on English without a redirect.
 */
export function detectLocale() {
  const stored = getStoredLocale();
  if (stored) return stored;

  if (typeof navigator === 'undefined') return null;
  const candidates = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const tag of candidates) {
    if (!tag) continue;
    const primary = tag.toLowerCase().split('-')[0];
    if (isLocale(primary)) return primary;
  }
  return null;
}
